import { DateTime } from "luxon";

export const monitoringStart = "2026-09-01";

export function monitoringToday(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now).map(({ type, value }) => [type, value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function shiftMonitoringDate(date, days) {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function monitoringRange(mode, anchor, today) {
  anchor = anchor < monitoringStart ? monitoringStart : anchor > today ? today : anchor;
  const d = new Date(`${anchor}T12:00:00Z`);
  let from = anchor;
  let to = anchor;
  if (mode === "week") {
    from = shiftMonitoringDate(anchor, -((d.getUTCDay() + 6) % 7));
    to = shiftMonitoringDate(from, 6);
  } else if (mode === "month") {
    from = anchor.slice(0, 7) + "-01";
    to = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 12)).toISOString().slice(0, 10);
  }
  return { from: from < monitoringStart ? monitoringStart : from, to: to > today ? today : to };
}

export function monitoringDates(from, to) {
  const days = [];
  for (let day = from; day <= to && days.length < 366; day = shiftMonitoringDate(day, 1)) {days.push(day);}
  return days;
}

export function monitoringDuration(seconds) {
  const minutes = Math.round(Math.max(0, Number(seconds) || 0) / 60);
  if (!minutes) {return seconds > 0 ? "<1m" : "0m";}
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;
}

export function monitoringDateLabel(date, options = {}) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC", month: "short", day: "numeric", ...options,
  });
}

export function monitoringTime(at, day) {
  if (!at) {return "—";}
  const date = new Date(at);
  const label = date.toLocaleTimeString("en-US", {
    timeZone: "America/New_York", hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  return day && date.getTime() === new Date(day.end).getTime() ? `${label} (+1 day)` : label;
}

export function monitoringEmptyDay(date) {
  const start = DateTime.fromISO(date, { zone: "America/New_York" }).startOf("day");
  return {
    day: date, start: start.toUTC().toISO(), end: start.plus({ days: 1 }).toUTC().toISO(),
    engagedSeconds: 0, callSeconds: 0, segments: [],
  };
}

export function monitoringMarkers(day) {
  const start = DateTime.fromISO(day.day, { zone: "America/New_York" });
  return [8, 18].map((hour) => ({
    label: hour === 8 ? "8am" : "6pm",
    position: monitoringPosition(start.set({ hour }).toUTC().toISO(), day),
  }));
}

export function monitoringTextsPerScheduled(texts, scheduled) {
  const count = Number(scheduled), total = Number(texts);
  return Number.isFinite(count) && count > 0 && Number.isFinite(total) && total >= 0 ? (total / count).toFixed(1) : "—";
}

export function monitoringAverages(person) {
  const days = (person.days || []).filter((day) => day.engagedSeconds >= 3600);
  return {
    days: days.length,
    seconds: days.length ? days.reduce((sum, day) => sum + day.engagedSeconds, 0) / days.length : null,
    scheduled: days.length ? person.scheduled / days.length : null,
  };
}

export function monitoringPosition(at, day) {
  const from = new Date(day.start).getTime();
  const until = new Date(day.end).getTime();
  return Math.max(0, Math.min(100, (new Date(at).getTime() - from) / (until - from) * 100));
}

export const activityCategories = [
  { key: "schedulingSeconds", label: "Scheduling", color: "#2563eb" },
  { key: "reviewSeconds", label: "Inspection review", color: "#16a34a" },
  { key: "homeSeconds", label: "Home / navigation", color: "#9ca3af" },
  { key: "otherSeconds", label: "Other pages", color: "#f59e0b" },
];

export function activityBarParts(totals) {
  const parts = activityCategories.map(({ key, label, color }) => ({
    key, label, color, seconds: Math.max(0, Number(totals?.[key]) || 0),
  })).filter((part) => part.seconds > 0);
  const total = parts.reduce((sum, part) => sum + part.seconds, 0);
  let offset = 0;
  parts.forEach((part) => {
    part.width = total ? part.seconds / total * 100 : 0;
    part.left = offset;
    offset += part.width;
  });
  return { parts, total };
}

export function activityPolicyPreview(policies, limit = 3) {
  const list = policies || [];
  const shown = list.slice(0, limit).join(", ");
  return list.length > limit ? `${shown} +${list.length - limit} more` : shown;
}

export function scoreBadgeClass(score) {
  if (score >= 8) { return "tw-bg-green-700 tw-text-white"; }
  if (score >= 5) { return "tw-bg-amber-600 tw-text-white"; }
  return "tw-bg-red-700 tw-text-white";
}

export function monitoringScoreLabel(score) {
  return `${Math.max(0, Math.min(10, Math.round(Number(score) || 0)))}/10`;
}
